import { z } from 'zod';

/** Stable, machine-readable error codes returned in RFC 9457 problem responses. */
export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'MALFORMED_REQUEST',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'ROUTE_NOT_FOUND',
  'METHOD_NOT_ALLOWED',
  'CONFLICT',
  'PAYLOAD_TOO_LARGE',
  'RATE_LIMITED',
  'SERVICE_UNAVAILABLE',
  /** Set by the web client, never the API: a 403 that is not ours (a network's web filter, ADR-0159). */
  'NETWORK_BLOCKED',
  'INTERNAL_ERROR',
  // Authentication (AUTH.md). Sign-in errors never reveal whether an account exists.
  'INVALID_CREDENTIALS',
  'ACCOUNT_DISABLED',
  'NO_MERCHANT_ACCESS',
  'INTERFACE_NOT_PERMITTED',
  // A PIN-opened POS session asked for /manager or another company (review RS-17).
  'PASSWORD_REQUIRED',
  'TICKET_INVALID',
  'MFA_INVALID',
  'RESET_TOKEN_INVALID',
  'EMAIL_ALREADY_REGISTERED',
  'ORIGIN_REJECTED',
  'VERSION_CONFLICT',
  'PRIVILEGE_ESCALATION',
  'INVITATION_INVALID',
  // Platform team (ADR-0157, BR-261).
  'LAST_SUPER_ADMIN',
  'OPERATOR_SELF_CHANGE',
  'ROLE_IN_USE',
  'BRANCH_REQUIRED',
  'DUPLICATE_CODE',
  'ALREADY_MEMBER',
  'DUPLICATE_SKU',
  'DUPLICATE_BARCODE',
  'INVALID_PRICE',
  'UNSUPPORTED_MEDIA',
  'INSUFFICIENT_STOCK',
  'NOT_STOCK_TRACKED',
  'OPENING_STOCK_EXISTS',
  'INVALID_QUANTITY',
  'IDEMPOTENCY_CONFLICT',
  'BRANCH_INACTIVE',
  'SHIFT_REQUIRED',
  'SHIFT_ALREADY_OPEN',
  'MOVEMENT_DECIDED',
  'PENDING_MOVEMENTS',
  'APPROVAL_REQUIRED',
  'ORDER_OVERPAID',
  'DEPOSIT_SETTLED',
  'ACCOUNT_IN_USE',
  'ACCOUNT_KIND_LOCKED',
  'ADJUSTMENT_DECIDED',
  'STOCK_MOVED_SINCE_COUNT',
  'STOCK_RECOUNTED',
  'STOCK_ON_HAND',
  'PERIOD_CLOSED',
  'OWNER_FROZEN',
  'OWNER_ONLY_SETTING',
  'TRANSFER_INVALID',
  'GRANTS_NEED_CONFIRMATION',
  'PERIOD_NOT_CLOSABLE',
  'TIMEZONE_LOCKED',
  'BRANCH_IN_USE',
  'ASSISTANT_DISABLED',
  'COST_UNKNOWN',
  'LOSS_DECIDED',
  'REGISTER_BUSY',
  'REGISTER_INACTIVE',
  'REGISTER_ACCESS_DENIED',
  'SHIFT_CLOSED',
  'INVALID_BREAKDOWN',
  'INSUFFICIENT_CASH',
  'INSUFFICIENT_TREASURY_CASH',
  'TREASURY_NOT_COUNTED',
  'EXPECTED_CHANGED',
  'REGISTER_HAS_CASH',
  'TRANSFER_NOT_ALLOWED',
  'INVALID_DISCOUNT',
  'DISCOUNT_LIMIT_EXCEEDED',
  'PRICE_OVERRIDE_DENIED',
  'PRICE_OUT_OF_BOUNDS',
  'ZERO_TOTAL_DENIED',
  'REASON_REQUIRED',
  'OVERRIDE_INVALID',
  'OVERRIDE_SELF',
  'OVERRIDE_NOT_ALLOWED',
  'REFERENCE_REQUIRED',
  'SETTLEMENT_ACCOUNT_REQUIRED',
  'PAYMENT_TOTAL_MISMATCH',
  'TOTAL_CHANGED',
  'PAYMENT_NOT_VERIFIED',
  'PAYMENT_METHOD_UNAVAILABLE',
  'ITEM_UNAVAILABLE',
  'ORDER_NOT_SUSPENDED',
  'REFUND_EXCEEDS_SALE',
  'REFUND_ALLOCATION_INVALID',
  'REFUND_NOT_PENDING',
  'NOT_VOIDABLE',
  'SELF_APPROVAL',
  'APPROVAL_REQUIRED',
  'INVALID_STATE',
  'RECEIVE_EXCEEDS_ORDER',
  'SUPPLIER_INACTIVE',
  'CUSTOMER_INACTIVE',
  'DUPLICATE_PHONE',
  'MODULE_DISABLED',
  'TABLE_OCCUPIED',
  'ITEM_ALREADY_PAID',
  'MODIFIER_INVALID',
  'STORE_CLOSED',
  'GATEWAY_NOT_CONFIGURED',
  'NO_PAYMENT_OPTION',
  'PAYMENT_METHOD_REQUIRED',
  'ORDER_UNPAID',
  'RENAME_LIMIT',
  'SLOT_FULL',
  'PLACE_TAKEN',
  'LAST_STORE_BRANCH',
  'OUTSIDE_HOURS',
  'PROMOTION_INVALID',
  'OPTIONS_INVALID',
  'ACCOUNTS_DISABLED',
  'EMAIL_TAKEN',
  'PHONE_TAKEN',
  'ASSET_IN_USE',
  'ASSET_LIMIT_REACHED',
  'PAYMENT_SIGNATURE_INVALID',
  'MINIMUM_ORDER',
  'PLAN_LIMIT_REACHED',
  'PLAN_FEATURE_UNAVAILABLE',
  'PLAN_CHANGE_BLOCKED',
  'MERCHANT_SUSPENDED',
  'SESSION_LOCKED',
  'UNLOCK_SHARED_ACCOUNT',
  'DEVICE_REVOKED',
  // Device upload refusals (ADR-0112 §5, docs/DEVICE_API.md).
  'NOT_OWN_SALE',
  'RECEIPT_NUMBER_INVALID',
  'RECEIPT_NUMBER_TAKEN',
  'TOTALS_MISMATCH',
  'UNKNOWN_VARIANT',
  'MEMBER_NOT_ALLOWED',
  'WRONG_DEVICE',
  'LINK_LIMIT_REACHED',
  'NOT_PRIMARY_DEVICE',
  'SHIFT_NOT_ACCEPTED',
  'CASH_NOT_ALLOWED',
  'REGISTER_BOUND',
  'PIN_LINK_INVALID',
  'PIN_NOT_SET',
  // Product discounts (ADR-0133)
  'DISCOUNT_ARCHIVED',
  // Own domains (ADR-0137).
  'DOMAINS_UNAVAILABLE',
  'DOMAINS_PROVIDER_ERROR',
  'DOMAIN_SEARCH_UNAVAILABLE',
  'DOMAIN_NOT_ALLOWED',
  'DOMAIN_TAKEN',
  'DOMAIN_ALREADY_CONNECTED',
  'DOMAIN_NOT_ACTIVE',
  'STORE_NOT_SAVED',
  // WhatsApp number proven by a code (ADR-0138 amendment 9)
  'WHATSAPP_UNAVAILABLE',
  'WHATSAPP_NOT_VERIFIED',
  'WHATSAPP_SEND_FAILED',
  'CODE_RESEND_TOO_SOON',
  'CODE_EXPIRED',
  'CODE_TOO_MANY_ATTEMPTS',
  'CODE_INVALID',
  // Connect with Google (owner decision 2026-11-03)
  'GOOGLE_CONNECT_UNAVAILABLE',
  'STORE_NOT_PUBLISHED',
  // Google features need the store's own domain (owner decision 2026-11-04)
  'CUSTOM_DOMAIN_REQUIRED',
  // Readable store addresses (migration 0107).
  'DUPLICATE_HANDLE',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

const validationIssueSchema = z.object({
  path: z.string(),
  code: z.string(),
  message: z.string(),
});
export type ValidationIssue = z.infer<typeof validationIssueSchema>;

/** RFC 9457 problem details as returned by the API (`application/problem+json`). */
export const problemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  code: z.string(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  requestId: z.string().optional(),
  issues: z.array(validationIssueSchema).optional(),
  /** Interfaces the user may use instead (with INTERFACE_NOT_PERMITTED). */
  availableInterfaces: z.array(z.enum(['manager', 'pos'])).optional(),
});
export type ProblemDetails = z.infer<typeof problemDetailsSchema>;
